// src/pages/AdminOverview.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminAPI from "../utils/adminApi";

const AdminOverview = () => {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const navigate = useNavigate();

  // Fetch stats (includes topReferrers)
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await AdminAPI.get("/stats");
        setStats(res.data);
      } catch (err) {
        console.error("❌ Error fetching stats", err.response?.data || err.message);
        if (err.response?.status === 401) {
          localStorage.removeItem("adminToken");
          navigate("/admin/login");
        }
      } finally {
        setLoadingStats(false);
      }
    };
    fetchStats();
  }, [navigate]);

  // Fetch logs
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const res = await AdminAPI.get("/logs");
        setLogs(res.data);
      } catch (err) {
        console.error("❌ Error fetching logs", err.response?.data || err.message);
        if (err.response?.status === 401) {
          localStorage.removeItem("adminToken");
          navigate("/admin/login");
        }
      } finally {
        setLoadingLogs(false);
      }
    };
    fetchLogs();
  }, [navigate]);

  if (loadingStats) return <p className="text-gray-500">Loading stats...</p>;

  return (
    <div className="space-y-10 p-6">
      {/* === Stats Cards === */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <h3 className="text-lg font-semibold text-gray-700">👥 Total Users</h3>
          <p className="text-2xl font-bold text-blue-600">{stats.totalUsers}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <h3 className="text-lg font-semibold text-gray-700">🏪 Vendors</h3>
          <p className="text-2xl font-bold text-green-600">{stats.totalVendors}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <h3 className="text-lg font-semibold text-gray-700">⚡ Admins</h3>
          <p className="text-2xl font-bold text-purple-600">{stats.totalAdmins}</p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow text-center">
          <h3 className="text-lg font-semibold text-gray-700">💰 Total Balance</h3>
          <p className="text-2xl font-bold text-pink-600">
            ₦{stats.totalBalance.toLocaleString()}
          </p>
        </div>
      </div>

      {/* === Top Referrers === */}
      <div>
        <h2 className="text-xl font-bold mb-4 text-gray-700">🏅 Top Referrers</h2>
        {stats.topReferrers?.length === 0 ? (
          <p className="text-gray-500">No referrers found.</p>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl shadow-lg p-4 max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-gray-600 font-semibold">Name</th>
                  <th className="px-4 py-2 text-left text-gray-600 font-semibold">Referral Code</th>
                  <th className="px-4 py-2 text-left text-gray-600 font-semibold">Referrals Count</th>
                  <th className="px-4 py-2 text-left text-gray-600 font-semibold">Bonus Earned (₦)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {stats.topReferrers?.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                    <td className="px-4 py-2 text-gray-700">{user.name}</td>
                    <td className="px-4 py-2 text-gray-700 font-medium">{user.referralCode}</td>
                    <td className="px-4 py-2 text-gray-700">{user.referralsCount}</td>
                    <td className="px-4 py-2 text-gray-700">₦{user.referralBonus.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* === Recent Activities === */}
      <div>
        <h2 className="text-xl font-bold mb-4 text-gray-700">📜 Recent Activities</h2>
        {loadingLogs ? (
          <p className="text-gray-500">Loading recent activities...</p>
        ) : logs.length === 0 ? (
          <p className="text-gray-500">No recent activities found.</p>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl shadow-lg p-4 max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left text-gray-600 font-semibold">Date</th>
                  <th className="px-4 py-2 text-left text-gray-600 font-semibold">User</th>
                  <th className="px-4 py-2 text-left text-gray-600 font-semibold">Type</th>
                  <th className="px-4 py-2 text-left text-gray-600 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {logs.map((log) => (
                  <tr key={log._id} className="hover:bg-gray-50 transition duration-150 ease-in-out">
                    <td className="px-4 py-2 text-gray-700">{new Date(log.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-gray-700">{log.userName || log.userEmail || "N/A"}</td>
                    <td className="px-4 py-2 text-gray-700 font-medium">{log.type}</td>
                    <td className="px-4 py-2 text-gray-700">{log.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminOverview;
